import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import type { Agent, AgentPlan, Channel, Message, MessagePage, ReadState } from '../api/types'
import { BACKOFF, type OnRoomFrame, useLiveMessages } from '../live/useLiveMessages'
import { countsAsUnread, unreadIn } from '../read/unread'
import { roomMessagesOf } from './format'

/** How many messages a room opens with. A three-day room rarely has more. */
const PAGE = 200

/**
 * The room with one message passed through `change`; the same array when it is
 * not held, so a frame about one costs no render.
 */
function updateMessage(messages: Message[], id: string,
                       change: (message: Message) => Message): Message[] {
  if (!messages.some((message) => message.id === id)) return messages
  return messages.map((message) => (message.id === id ? change(message) : message))
}

export interface Room {
  messages: Message[]
  roster: Agent[]
  /** What each running agent says it is working through, by agent name. Only the stream sends it. */
  plans: Record<string, AgentPlan>
  /**
   * What you have not read of the messages held, thread replies included. A
   * message leaves it as it is seen, and one arriving joins it when it can be
   * unread at all.
   */
  unread: ReadonlySet<string>
  /**
   * The first unread message of the room's own rows when the room loaded,
   * where the New line goes. Fixed until the room is left, so what was read
   * stays below the line. Null until this room's own page has landed.
   */
  newLineBefore: string | null
  /** Seen in the room or a thread: out of `unread` at once, and sent. */
  markSeen: (ids: string[]) => void
  /** Everything up to `through` read, or up to the room's newest message: the phone's Mark read, and the Unreads list's. */
  markRead: (through?: string) => void
  /** The room's first page would not load, and another attempt is on its way. */
  loadFailed: boolean
  /** Post to the room, as a reply in the thread of `replyTo` when it names a message, and shown in the room as well with `alsoInRoom`. */
  post: (body: string, replyTo?: string | null, alsoInRoom?: boolean) => Promise<Message | null>
  replaceMessage: (updated: Message) => void
  /** Every frame the stream delivers, replays included, for a view that plays the events. */
  onRoomFrame: OnRoomFrame
}

/**
 * What is in the room you are looking at, and what of it `reader`, the
 * person's display name, has not read.
 *
 * Opening a room reads nothing: seeing a message does (`useSeen`), so the
 * room only takes the read state in beside its page.
 */
export function useRoom(channel: Channel | null, reader: string): Room {
  const [messages, setMessages] = useState<Message[]>([])
  const [roster, setRoster] = useState<Agent[]>([])
  const [plans, setPlans] = useState<Record<string, AgentPlan>>({})
  const [unread, setUnread] = useState<ReadonlySet<string>>(new Set())
  const [newLineBefore, setNewLineBefore] = useState<string | null>(null)

  const channelId = channel?.id ?? null

  // Read inside effects and the live handler, neither of which should re-run
  // when these change: reloading a room because its unread count moved is the
  // loop this indirection exists to prevent.
  const channelRef = useRef(channel)
  channelRef.current = channel
  const readerRef = useRef(reader)
  readerRef.current = reader
  // Every id the room has held since its page landed. A reconnecting stream
  // resumes from a cursor and replays what came after it, and a replayed
  // message must not come back as unread after it was read.
  const held = useRef(new Set<string>())
  // A roster the stream sent is at least as new as the page load's, which may land after it.
  const rosterPushed = useRef(false)
  // Which room the page in `messages` belongs to. The stream waits for it and
  // opens from the last message on that page: opened any sooner it has no
  // cursor, and the server replays the whole room from its first message.
  const [loadedChannelId, setLoadedChannelId] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)

  useEffect(() => {
    const open = channelRef.current
    // The last room's plans are not this one's, and this room's arrive with its stream.
    setPlans({})
    // Nor is its read state. Its page stays on screen until this one lands,
    // rather than an empty room saying nothing was ever said in it.
    setUnread(new Set())
    setNewLineBefore(null)
    if (!open || !channelId) {
      setMessages([])
      setRoster([])
      return
    }
    let current = true
    let attempt = 0
    let retry: ReturnType<typeof setTimeout> | undefined
    rosterPushed.current = false
    setLoadFailed(false)

    // The stream waits for this page, so a load that fails and stops there
    // leaves the room empty and deaf for good. It is tried again the way the
    // stream reconnects, and the room says why it is empty in the meantime.
    const load = async () => {
      let page: MessagePage
      let people: Agent[]
      let state: ReadState
      try {
        [page, people, state] = await Promise.all([
          api.messages(open.id, { threaded: false, limit: PAGE }),
          api.agents(open.id),
          api.seen(open.id),
        ])
      } catch {
        if (!current) return
        // Until this room loads, what is held is the last room's page and
        // roster, and the stream must not take that page as one of any room
        // it goes back to.
        setMessages([])
        setRoster([])
        setLoadedChannelId(null)
        setLoadFailed(true)
        retry = setTimeout(load, BACKOFF[Math.min(attempt++, BACKOFF.length - 1)])
        return
      }
      if (!current) return
      setLoadFailed(false)
      setMessages(page.messages)
      setLoadedChannelId(open.id)
      if (!rosterPushed.current) setRoster(people)
      held.current = new Set(page.messages.map((message) => message.id))
      const fresh = unreadIn(page.messages, state, readerRef.current)
      setUnread(fresh)
      setNewLineBefore(roomMessagesOf(page.messages).find((message) => fresh.has(message.id))?.id ?? null)
    }
    void load()
    // Ignore a response that arrives after the room has changed under it.
    return () => {
      current = false
      clearTimeout(retry)
    }
  }, [channelId])

  const onRoomFrame = useLiveMessages(
    loadedChannelId === channelId ? channelId : null,
    messages.length ? messages[messages.length - 1].id : null,
    useCallback((arrived: Message) => {
      setMessages((previous) => {
        // A reconnecting stream resumes from a cursor and replays what came
        // after it, so the room sees messages it already holds and has to know
        // them by sight.
        if (previous.some((message) => message.id === arrived.id)) return previous
        return [...previous, arrived]
      })
      if (held.current.has(arrived.id)) return
      held.current.add(arrived.id)
      if (countsAsUnread(arrived, readerRef.current)) setUnread((previous) => new Set(previous).add(arrived.id))
    }, []),
    {
      reactions: ({ message_id, reactions }) => setMessages((previous) =>
        updateMessage(previous, message_id, (message) => ({ ...message, reactions }))),
      edited: (edited) => setMessages((previous) => updateMessage(previous, edited.id, () => edited)),
      // An agent joins by its first heartbeat, usually long before it speaks,
      // so a stranger talking needs no refetch.
      presence: (agents) => {
        rosterPushed.current = true
        setRoster(agents)
      },
      plans: (arrived) => setPlans(Object.fromEntries(arrived.map((plan) => [plan.agent, plan]))),
      synced: (named) => setMessages((previous) => {
        const stale = (message: Message) => message.reactions.length > 0 && !named.has(message.id)
        if (!previous.some(stale)) return previous
        return previous.map((message) => (stale(message) ? { ...message, reactions: [] } : message))
      }),
    },
  )

  const post = useCallback(async (body: string, replyTo: string | null = null, alsoInRoom = false) => {
    const open = channelRef.current
    if (!open) return null
    const created = await api.post(open.id, { body, reply_to: replyTo, also_in_room: alsoInRoom })
    // Its own frame may have come over the stream first.
    setMessages((previous) => (previous.some((message) => message.id === created.id) ? previous : [...previous, created]))
    return created
  }, [])

  /**
   * Swap one message for a newer copy of itself.
   *
   * A reaction comes back as the whole message rather than as a patch, so the
   * room never has to reconstruct what the server already worked out.
   */
  const replaceMessage = useCallback((updated: Message) => {
    setMessages((previous) => updateMessage(previous, updated.id, () => updated))
  }, [])

  // Sent for the room whose page holds what was seen, which just after a
  // switch is still the last one.
  const markSeen = useCallback((ids: string[]) => {
    if (!loadedChannelId) return
    setUnread((previous) => {
      const left = new Set(previous)
      for (const id of ids) left.delete(id)
      return left
    })
    void api.markSeen(loadedChannelId, ids)
  }, [loadedChannelId])

  const markRead = useCallback((through?: string) => {
    const last = through ?? messages[messages.length - 1]?.id
    if (!loadedChannelId || !last) return
    setUnread((previous) => new Set([...previous].filter((id) => id > last)))
    void api.markRead(loadedChannelId, last)
  }, [loadedChannelId, messages])

  return {
    messages, roster, plans, unread, markSeen, markRead, loadFailed, post, replaceMessage, onRoomFrame,
    // The render that switches rooms still holds the last room's, which
    // clearing it in the load effect only takes away a render later, too late
    // for a view that opens at it in that render.
    newLineBefore: loadedChannelId === channelId ? newLineBefore : null,
  }
}
